import classnames from "classnames";
import {
  filterPathIntersectionCached,
  findPathIntersectionCached,
  following_are_optionals,
} from "oc-app/metadata/GraphMetadata";
import * as Commons from "oc-commons/Commons";
import { I18N } from "oc-I18N";
import * as Maths3 from "oc-threejs/Maths3";
import { v2 } from "oc-threejs/short3";
import * as Doms from "oc-ui/Doms";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { useEffect, useRef, useState } from "react";
import {
  elementOrTor,
  flügelbreiteZuAussenbreite,
  getElementMetasGleicherForm,
  isDoppeltorFn,
  sizeApproximatelyEpsilon,
} from "../../arrangement/elementFunctions";
import { getImageDataPromise, getVariantUniqueId } from "../sceneeditor/ImageCreator";
import { ZElement } from "../../arrangement/ZElement";
import { Arrangement } from "../../arrangement/Arrangement";
import { Grundriss } from "../../arrangement/Grundriss";
import {
  MAuswahlwert,
  MElement,
  MElementThumbnails,
  MMass,
  MObjekt,
  MTor,
} from "../../../metamodell";
import { PAuswahlwert, PElement, PMass, PSerie, PTor } from "../../../metamodell_paths";
import { debounce, maxBy, minBy, sortBy } from "lodash";
import {
  getHighestScoredMeta,
  getWunschBreiteAussenabstand,
  matchFüllungWithHistory,
  sizeScore,
} from "../../arrangement/elementScoring";
import { ExclamationSvg } from "../../inlinesvgs/ExclamationSvg";
import { Auswahl, findAuswahlwerte } from "../Auswahl";

function hasSenkrechtprofileInSerie(): boolean {
  const agt = APP.arrangement;
  const formen: MObjekt[] = filterPathIntersectionCached("ZaunElementList:hasSenkrecht")(
    agt.serie,
    (p: PSerie) => elementOrTor(p.DetailSerie).Mass.Form
  );
  return formen.some((form) => form.Key.endsWith("_Senkrecht"));
}

export function ZaunElementList() {
  const showToggle = hasSenkrechtprofileInSerie();
  const senkrechtAuswahlwerte = showToggle
    ? findAuswahlwerte({ Key: "Senkrechtprofile_Easy" }).auswahlwerte
    : [];
  const defaultAuswahlwert = senkrechtAuswahlwerte?.find((a) => a.Key === "Waagerecht") ?? senkrechtAuswahlwerte?.[0];
  const [senkrechtAuswahlwert, setSenkrechtAuswahlwert] = useState<MAuswahlwert>(defaultAuswahlwert);
  const isSenkrecht = senkrechtAuswahlwert?.Key === "Senkrecht";

  useEffect(() => {
    APP.on("selectZaunelement", onSelectElement);
    APP.on("changeZaunelement", onSelectElement);

    return () => {
      APP.off("selectZaunelement", onSelectElement);
      APP.off("changeZaunelement", onSelectElement);
    };
  }, []);

  return (
    <div className="zaunfelder">
      {showToggle && (
        <div className="senkrecht-toggle">
          <Auswahl
            Key="Senkrechtprofile_Easy"
            type="toggle"
            value={senkrechtAuswahlwert}
            auswahlwerte={senkrechtAuswahlwerte}
            onChange={(v) => setSenkrechtAuswahlwert(v)}
          />
        </div>
      )}
      <div
        className={classnames("zaunElements", "fade")}
        role="radiogroup"
        aria-label={I18N("aria::Zaunelement auswählen")}
      >
        {APP.arrangement.isElementeEinzelnListen ? (
          <SingleDetailserie isSenkrecht={showToggle && isSenkrecht} />
        ) : (
          <>{MultiDetailSerie(showToggle && isSenkrecht)}</>
        )}
      </div>
    </div>
  );
}

function SingleDetailserie({ isSenkrecht }: { isSenkrecht: boolean }) {
  const agt = APP.arrangement;
  const selectedMassauswahlwertKey = findBestMatchingMassauswahlwertKey(agt.selectedObjects[0]);
  const allElementMetas: (MElement | MTor)[] = filterPathIntersectionCached("ZaunElementList.tsx:57")(
    agt.füllungAuswahlwert,
    (p) => elementOrTor((p as any).Füllung_Objekt)
  );
  const elementMetas = allElementMetas.filter((elementMeta) => {
    const formIsSenkrecht = elementMeta.Mass?.Form?.Key?.endsWith("_Senkrecht") ?? false;
    return formIsSenkrecht === isSenkrecht;
  });
  const elementPriority = ["fix", "fertigung", "bauseits"];
  const elementeFüllungen = sortBy(
    [
      ...elementMetas
        .flatMap((elementMeta) =>
          elementMeta.Füllung.flatMap((füllungAuswahlwert) => ({
            massAuswahlwert: elementMeta.Mass.EasyAuswahlwert,
            füllungAuswahlwert: füllungAuswahlwert.Auswahlwert,
            name: elementMeta.Mass.EasyAuswahlwert.Name,
            mass: elementMeta.Mass,
            dataKey: elementMeta.Key,
            isSelected: elementMeta.Key == selectedMassauswahlwertKey,
          }))
        )
        .filter(
          ({ massAuswahlwert, füllungAuswahlwert }) =>
            !massAuswahlwert.Gesperrt && !füllungAuswahlwert.Gesperrt
        )
        .reduce((map, element) => {
          if (!map.has(element.name) || element.isSelected) {
            map.set(element.name, element);
          } else {
            const other = map.get(element.name);
            if (!other.isSelected) {
              const toBeSet = [other, element].sort((a, b) =>
                Math.sign(
                  elementPriority.indexOf(a.mass.Variabel) -
                    elementPriority.indexOf(b.mass.Variabel)
                )
              )[0];
              map.set(element.name, toBeSet);
            }
          }
          return map;
        }, new Map())
        .values(),
    ],
    (e) => e.massAuswahlwert.Sortierung
  );
  return (
    <>
      {elementeFüllungen.map((element, i) => {
        return (
          <ZaunelementPreview
            key={element.massAuswahlwert.Key + "_" + i}
            {...element}
            auswahlSetter={toastIfNothingSelected(() => {
              agt.changeSelectedZaunElemente({
                füllungAuswahlwert: element.füllungAuswahlwert,
                massAuswahlwert: element.massAuswahlwert,
              });
              APP.logUserAction({ action: "auswahl", Form: element.form });
            })}
          />
        );
      })}
    </>
  );
}

function createElement(massAuswahlwert: MAuswahlwert, füllungAuswahlwert: MAuswahlwert) {
  const agtClone = createArrangementClone();
  const element = agtClone.createElement({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  element.updateMetas();
  return element;
}

function MultiDetailSerie(isSenkrecht: boolean): (JSX.Element | null)[] {
  const agt = APP.arrangement;
  const selectedMassauswahlwertKey = findBestMatchingMassauswahlwertKey(agt.selectedObjects[0]);

  const massAuswahlwerte = filterPathIntersectionCached("ZaunElementList.tsx:110")(
    agt.serie,
    (p: PSerie) => elementOrTor(p.DetailSerie).Mass.EasyAuswahlwert
  ) as MAuswahlwert[];

  return massAuswahlwerte
    .filter((massAuswahlwert) => !massAuswahlwert.Gesperrt)
    .filter((massAuswahlwert) => {
      const form = findPathIntersectionCached("ZaunElementList:formFilter")(
        massAuswahlwert,
        (p: PAuswahlwert) => p.Mass.Form
      ) as MObjekt;
      const formIsSenkrecht = form?.Key?.endsWith("_Senkrecht") ?? false;
      return formIsSenkrecht === isSenkrecht;
    })
    .map((massAuswahlwert) => {
      const partition = partitionFüllungenForMass(massAuswahlwert, agt);
      const { possibleFüllungElementMetas, andereElemente, keineElemente, nichtAlsFüllung } =
        partition;
      const füllungAuswahlwert = findBestFüllung(massAuswahlwert, agt);
      const matchesFüllung = füllungAuswahlwert == agt.füllungAuswahlwert;
      const title = getElementTooltip(partition, matchesFüllung);

      if (matchesFüllung && nichtAlsFüllung) {
        return null;
      }

      return (
        <ZaunelementPreview
          key={massAuswahlwert.Key}
          name={massAuswahlwert.Name}
          massAuswahlwert={massAuswahlwert}
          füllungAuswahlwert={füllungAuswahlwert}
          error={partition.keineElemente}
          warning={nichtAlsFüllung}
          title={title}
          isSelected={selectedMassauswahlwertKey == massAuswahlwert.Key}
          auswahlSetter={toastIfNothingSelected(async () => {
            await agt.changeSelectedZaunElemente({
              massAuswahlwert,
              füllungAuswahlwert,
            });
            const Form = findPathIntersectionCached("ZaunElementList:171")(
              massAuswahlwert,
              (p: PAuswahlwert) => p.Mass.Form.Key
            );
            APP.logUserAction({ action: "auswahl", Form });
            await Commons.sleep(1000);
            APP.trigger("maybeShowTorOptionen");
          })}
        />
      );
    });
}

function partitionFüllungenForMass(massAuswahlwert: MAuswahlwert, agt: Arrangement) {
  const possibleElementMetas: (MTor | MElement)[] = filterPathIntersectionCached(
    "ZaunElementList.tsx:147"
  )(
    /* @ts-ignore */
    ...[
      [massAuswahlwert, (p: PAuswahlwert) => elementOrTor(p.Mass)],
      [agt.serie, (p: PSerie) => elementOrTor(p.DetailSerie)],
    ]
      .filter(([v]) => v != null)
      .flat()
  );
  const possibleFüllungElementMetas: (MTor | MElement)[] = filterPathIntersectionCached(
    "ZaunElementList.tsx:155"
  )(
    /* @ts-ignore */
    ...[
      [massAuswahlwert, (p: PAuswahlwert) => elementOrTor(p.Mass)],
      [agt.serie, (p: PSerie) => elementOrTor(p.DetailSerie)],
      [
        agt.selectedObjects[0]?.isElement ? agt.selectedObjects[0]?.meta : null,
        (p: PElement) => elementOrTor(p.DetailSerie),
      ],
      [agt.füllungAuswahlwert, (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt)],
    ]
      .filter(([v]) => v != null)
      .flat()
  );

  const andereElemente = new Set(possibleElementMetas).difference(
    new Set(possibleFüllungElementMetas)
  );
  const keineElemente = possibleElementMetas.length == 0;
  const nichtAlsFüllung = possibleFüllungElementMetas.length == 0;

  return {
    possibleFüllungElementMetas,
    andereElemente,
    keineElemente,
    nichtAlsFüllung,
  };
}

function getElementTooltip(
  {
    possibleFüllungElementMetas,
    andereElemente,
    keineElemente,
    nichtAlsFüllung,
  }: ReturnType<typeof partitionFüllungenForMass>,
  matchesFüllung: boolean
) {
  const formatList = (metas) => metas.map((e) => e.Key).join(",\n");

  const isAdmin = APP.settings.userRole == "admin";
  const getAdminTooltip = () =>
    nichtAlsFüllung
      ? `${I18N("Dieses Mass gibt es nicht für diese Füllung")}\n\n${I18N(
          "Elemente mit anderer Füllung:"
        )}\n\n${formatList(andereElemente)}`
      : `${I18N("Elemente mit passender Füllung:")}\n\n${formatList(
          possibleFüllungElementMetas
        )}\n\n${I18N("Elemente mit anderer Füllung:")}\n\n${formatList(andereElemente)}`;

  const getUserTooltip = () =>
    nichtAlsFüllung && !matchesFüllung
      ? I18N("Dieses Maß ist nur in anderen Serien verfügbar")
      : ""; //passendes Maßelement

  return keineElemente
    ? I18N(
        "darf gar nicht zur Auswahl stehen, da keine Elemente mit diesem Maß in dieser Serie existieren!"
      )
    : isAdmin
      ? getAdminTooltip()
      : getUserTooltip();
}

/**
 * Reduced copy from element.change({massauswahlwert, elementPreview=true});
 */
function findBestFüllung(massAuswahlwert: MAuswahlwert, arrangement: Arrangement): MAuswahlwert {
  // der Maßauswahlwert hat zwei Aspekte: 1. Form, 2. Size
  const form = findPathIntersectionCached("ZaunElementList.tsx:221")(
    massAuswahlwert,
    (p: PAuswahlwert) => p.Mass.Form
  ) as MObjekt;

  const elementeDerSerieGleicherForm: MElement[] = getElementMetasGleicherForm(form);
  const serieMassMetas: MMass[] = filterPathIntersectionCached("ZaunElementList.tsx:224")(
    elementeDerSerieGleicherForm,
    (p: PElement) => p.Mass
  );
  const standardMassMetas: MMass[] = filterPathIntersectionCached("ZaunElementList.tsx:228")(
    massAuswahlwert,
    (p: PAuswahlwert) => p.Mass,
    serieMassMetas,
    (p: PMass) => p,
    following_are_optionals,
    arrangement.füllungAuswahlwert,
    (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt).Mass
  );
  const nichtFertigungsMasse = standardMassMetas.filter((m) => m.Variabel != "fertigung");
  // "Aussenabstand" ist immer von Nachbar-Element-Kante|Segmentende|Mauer von Seite zu Seite, ohne Pfosten
  const standardMasse = nichtFertigungsMasse.length > 0 ? nichtFertigungsMasse : standardMassMetas;
  const maxStandardSize = maxBy(standardMasse, (m) => {
    return m.Standard.x;
  }).Standard;
  const getWunschBreiteAussenabstandUndHöhe = (elementMeta: MElement | MTor) => {
    const { x, y } = (() => {
      const standard = standardMasse.includes(elementMeta.Mass)
        ? elementMeta.Mass.Standard
        : maxStandardSize;
      const aussenbreite = flügelbreiteZuAussenbreite(isDoppeltorFn(elementMeta), standard.x);
      return v2(aussenbreite, standard.y);
    })();
    return v2(getWunschBreiteAussenabstand(x, false, 0), y);
  };
  const sizeScoreMeta = (elementMeta: MElement | MTor) =>
    sizeScore(elementMeta, getWunschBreiteAussenabstandUndHöhe(elementMeta), 0);
  const füllungScoreMeta = (elementMeta: MElement | MTor) =>
    matchFüllungWithHistory(
      elementMeta,
      arrangement.füllungAuswahlwert,
      arrangement.füllungAuswahlwertHistory
    );
  const score = (elementMeta: MElement | MTor) =>
    sizeScoreMeta(elementMeta) + füllungScoreMeta(elementMeta).score * 0.01;
  const winnerMeta = getHighestScoredMeta(elementeDerSerieGleicherForm, score);
  return füllungScoreMeta(winnerMeta).füllungAuswahlwert;
}

function findBestMatchingMassauswahlwertKey(selected: ZElement) {
  if (selected == null) {
    return null;
  }

  const agt = selected.arrangement as unknown as Arrangement;
  if (agt.isElementeEinzelnListen) {
    return selected.isElement ? selected.meta?.Key : null;
  }
  if (selected.massAuswahlwert?.Mass?.[0]?.Form?.Key == "Rechteck") {
    const masse = (
      filterPathIntersectionCached("ZaunElementList.tsx:281")(
        agt.serie,
        (p: PSerie) => elementOrTor(p.DetailSerie).Mass
      ) as MMass[]
    ).filter((m) => m.Form.Key == "Rechteck");
    const size = Maths3.v3To2xy(selected.size);
    const sizeMin = size.clone().sub(v2(sizeApproximatelyEpsilon, sizeApproximatelyEpsilon));
    const größereMaße = masse.filter((m) => {
      return m.Standard.x > sizeMin.x && m.Standard.y > sizeMin.y;
    });
    const betrachteteMaße = größereMaße.length > 0 ? größereMaße : masse;
    const mass = minBy(betrachteteMaße, (m) => {
      return m.Standard.distanceTo(size);
    });
    return mass.EasyAuswahlwert.Key;
  }
  return selected.massAuswahlwert?.Key;
}

const onSelectElement = debounce(() => {
  const agt = APP.arrangement;
  const selected = agt.selectedObjects[0];
  if (!selected) {
    return;
  }
  let key = findBestMatchingMassauswahlwertKey(selected);
  const element = document.querySelector(`.zaunElement[data-key^="${key}"]`) as HTMLElement;
  const container = element?.parentElement?.parentElement?.parentElement;
  if (container) {
    Doms.scrollIntoViewIfNeeded(element, container, { behavior: "smooth" }, false, true);
  }
}, 100);

const toastIfNothingSelected = (setter: () => void | Promise<void>) => () => {
  if (APP.arrangement.selectedObjects[0] == null) {
    Dialogs.showToast(I18N("Klicken Sie zuerst auf ein Element im Zaun, das Sie ersetzen möchten"));
  } else {
    setter();
  }
};

type ZaunelementPreviewProps = {
  massAuswahlwert?: MAuswahlwert;
  füllungAuswahlwert?: MAuswahlwert;
  dataKey?: string;
  name: string;
  title?: string;
  isSelected: boolean;
  warning?: boolean;
  error?: boolean;
  auswahlSetter: () => void;
};

const ZaunelementPreview = ({
  massAuswahlwert,
  füllungAuswahlwert,
  dataKey,
  name,
  isSelected,
  auswahlSetter,
  title = name,
  warning,
  error,
}: ZaunelementPreviewProps) => {
  const variantUniqueId = getVariantUniqueId({ massAuswahlwert, füllungAuswahlwert });
  const thumbnail = findPathIntersectionCached("ZaunElementList.tsx:345")(
    massAuswahlwert,
    (p: PAuswahlwert) => p.ElementThumbnails,
    füllungAuswahlwert,
    (p: PAuswahlwert) => p.ElementThumbnails
  ) as MElementThumbnails;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (thumbnail) {
      const imageDiv = ref.current;
      if (imageDiv) {
        imageDiv.style.backgroundImage = `url(${thumbnail.Thumbnail})`;
      }
    } else {
      // console.debug("NOT FOUND", massAuswahlwert, füllungAuswahlwert);
      const zaunElement = createElement(massAuswahlwert, füllungAuswahlwert);
      const variantUniqueId = getVariantUniqueId({ massAuswahlwert, füllungAuswahlwert });
      getImageDataPromise(zaunElement, variantUniqueId).then((image) => {
        const imageDiv = ref.current;
        if (imageDiv) {
          imageDiv.style.backgroundImage = `url(${image})`;
        }
      });
    }
  }, [variantUniqueId, thumbnail, massAuswahlwert, füllungAuswahlwert]);

  const handleActivate = () => {
    auswahlSetter();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleActivate();
    }
  };

  return (
    <div className="zaunelement-container" data-auswahl-key={"EasyMass"}>
      <div
        data-auswahlwert-key={massAuswahlwert.Key}
        key={massAuswahlwert.Key}
        title={title}
        className={classnames(
          "zaunElement",
          isSelected && "selected",
          error ? "--error" : warning ? "--warning" : ""
        )}
        data-key={dataKey ?? massAuswahlwert.Key}
        data-variant-key={variantUniqueId}
        onPointerUp={handleActivate}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="radio"
        aria-checked={isSelected}
        aria-label={massAuswahlwert.AriaText || massAuswahlwert.Name}
      >
        <div className={"img"} ref={ref} />
        {(error || warning) && <ExclamationSvg />}
      </div>
      <div className="label" dangerouslySetInnerHTML={{ __html: name }}></div>
    </div>
  );
};

function createArrangementClone() {
  const clone = new Arrangement();
  const json = {
    ...APP.arrangement.toJSON(),
    segmente: [],
    ecken: [],
    aufbauten: [],
    bestellliste: [],
    baugruppen: [],
    gersamtpreis: 0,
    warnungen: [],
    montageanleitungen: [],
    grundriss: new Grundriss().setStandardForm("Strecke", [3]).toJSON(),
  };
  clone.fromJSON(json);
  clone.update3d();
  // ist ok, weil dieser clone eh' nur einmal verwendet wird
  clone.ghostPool.start();
  return clone;
}
